import React, { useState, useEffect } from "react";
import {
    Avatar, Grid, Typography, IconButton, Modal, Button, Slider, Box
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import CloseIcon from "@mui/icons-material/Close";
import Cropper from "react-easy-crop";
import { connect } from "react-redux";
import { UserDetailsViewModel } from "../../../configuration/api/apiSchema";
import { HandymanAppState } from "../../../configuration/redux/reducers";
import { getUserDetails, updateUserAvatar } from "../../Auth/actions";
import PreviewImageModal from "../../../components/PreviewImage/PreviewImageModal";
import { Link } from 'react-router-dom';

interface StateFromProps {
    userDetails?: UserDetailsViewModel;
    isUserLoading: boolean;
    isAvatarUpdated: boolean;
}

interface DispatchFromProps {
    updateUserAvatar: (avatarBase64: string) => void;
    getUserDetails: (userId?: string) => void;
}

interface PassedProps {
    userId?: string;
}

type Props = StateFromProps & DispatchFromProps & PassedProps;

const AvatarSection = (props: Props) => {
    const isReadonly = props.userId !== undefined;

    const [image, setImage] = useState<string | null>(null);
    const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState<number>(1);
    const [cropAreaPixels, setCropAreaPixels] = useState<any>(null);
    const [isCropModalOpen, setIsCropModalOpen] = useState<boolean>(false);

    const [isImageModalOpen, setIsImageModalOpen] = useState<boolean>(false);
    const [base64SelectedImage, setBase64SelectedImage] = useState<string | null>(null);

    const handleImageClick = (src: string): void => {
        setBase64SelectedImage(src);
        setIsImageModalOpen(true);
    };

    const handleCloseImageModal = (): void => {
        setIsImageModalOpen(false);
        setBase64SelectedImage(null);
    };

    useEffect(() => {
        if (props.isAvatarUpdated) {
            props.getUserDetails()
        }
    }, [props.isAvatarUpdated])

    // to change data when user go to his own profile or browse handyman
    useEffect(() => {
        if (props.userId) {
            props.getUserDetails(props.userId)
        } else {
            props.getUserDetails()
        }
    }, [props.userId])

    // If your API puts the user’s avatar in userDetails.avatarUrl (or just .avatar)
    // we’ll track that here:
    const avatarSrc = props.userDetails?.avatarUrl || "";
    const userName = `${props.userDetails?.firstName || ""} ${props.userDetails?.lastName || ""}`;
    // const userId = userDetails?.id || "";

    // Called when user picks a new file
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setImage(URL.createObjectURL(file));
            setIsCropModalOpen(true);
        }
    };

    // Called when user finishes cropping
    const onCropComplete = (_croppedArea: any, croppedAreaPixels: any) => {
        setCropAreaPixels(croppedAreaPixels);
    };

    // Called when user presses “Save” in modal
    const handleCropComplete = async () => {
        if (image && cropAreaPixels) {
            const croppedImgBase64 = await getCroppedImg(image, crop, cropAreaPixels);
            if (croppedImgBase64) {
                // Dispatch to Redux action so it updates the server & store
                props.updateUserAvatar(croppedImgBase64);
            }
            setIsCropModalOpen(false);
        }
    };

    return (
        <>
            <PreviewImageModal
                base64SelectedImage={base64SelectedImage}
                handleCloseImageModal={handleCloseImageModal}
                isImageModalOpen={isImageModalOpen}
            />
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3} textAlign="center">
                    <Box position="relative" display="inline-block">
                        <Avatar
                            src={avatarSrc}
                            alt={userName}
                            sx={{ width: 120, height: 120, margin: "0 auto", cursor: 'pointer', }}
                            onClick={() => handleImageClick(avatarSrc)}
                        />
                        {
                            !isReadonly && <IconButton
                                component="label"
                                sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    right: 0,
                                    backgroundColor: "white",
                                    borderRadius: "50%",
                                }}
                            >
                                <PhotoCameraIcon />
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </IconButton>
                        }

                    </Box>
                </Grid>

                <Grid item xs={12} sm={9}>
                    <Typography variant="h5">{userName || "User Name"}</Typography>
                    <Typography color="textSecondary">
                        Account ID: #332441
                    </Typography>
                    {
                        !isReadonly && <Typography color="textSecondary">
                            To see a public version of your account click{" "}
                            <Link to={`/browse-handyman/${props.userDetails?.id}`}>here</Link>.
                        </Typography>
                    }

                </Grid>
            </Grid>

            {/* Crop Modal */}
            <Modal open={isCropModalOpen && !isReadonly} onClose={() => setIsCropModalOpen(false)}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "90%", sm: 500 },
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        maxHeight: "80vh",
                        overflowY: "auto",
                    }}
                >
                    <IconButton
                        onClick={() => setIsCropModalOpen(false)}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h6" gutterBottom>
                        Adjust and Crop Image
                    </Typography>

                    <Box sx={{ position: "relative", width: "100%", height: 300, mt: 2 }}>
                        {image && (
                            <Cropper
                                image={image}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </Box>

                    <Slider
                        value={zoom}
                        min={1}
                        max={3}
                        step={0.1}
                        onChange={(e, value) => setZoom(value as number)}
                        sx={{ mt: 3 }}
                    />

                    <Button
                        variant="contained"
                        onClick={handleCropComplete}
                        sx={{ mt: 2 }}
                    >
                        Save
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

/** 
 * Connect to Redux
 */

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
    userDetails: state.user.userDetails,
    isUserLoading: state.user.isLoading,
    isAvatarUpdated: state.user.isAvatarUpdated
});

const mapDispatchToProps: DispatchFromProps = {
    updateUserAvatar,
    getUserDetails
};

export default connect<
    StateFromProps,
    DispatchFromProps,
    PassedProps,
    HandymanAppState
>(mapStateToProps, mapDispatchToProps)(AvatarSection);


// Optionally rename to getCroppedImgBase64
export const getCroppedImg = async (
    imageSrc: string,
    crop: { x: number; y: number },
    cropAreaPixels: any
): Promise<string | null> => {
    const image = new Image();
    image.src = imageSrc;

    await new Promise((resolve) => {
        image.onload = resolve;
    });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    canvas.width = cropAreaPixels.width;
    canvas.height = cropAreaPixels.height;

    ctx.drawImage(
        image,
        cropAreaPixels.x,
        cropAreaPixels.y,
        cropAreaPixels.width,
        cropAreaPixels.height,
        0,
        0,
        cropAreaPixels.width,
        cropAreaPixels.height
    );

    // Return a base64 string
    return canvas.toDataURL("image/jpeg");
};


